import {Component, Input, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {UntypedFormGroup} from '@angular/forms';
import {
  getPropertyDescription,
  getPropertyTuples,
  hasEqualValue,
  hasValue,
  propertyValid,
  Subscribe,
  typeFilter,
} from '@aztrix/helpers';
import {AgreementVerificationType, Property, PropertyType} from '@aztrix/models';
import {
  AgreementRepresentation,
  ProfileRepresentation,
  PropertyRepresentation,
  ProposalLanguageRepresentation,
  ProposalRepresentation,
} from '@aztrix/sdk';
import {Subscription} from 'rxjs';

@Component({
  selector: 'ax-proposal-subscribe-verification-form',
  templateUrl: './proposal-subscribe-verification-form.component.html',
  styleUrls: ['./proposal-subscribe-verification-form.component.scss'],
})
export class ProposalSubscribeVerificationFormComponent implements OnChanges, OnDestroy {
  @Input() form: UntypedFormGroup;
  @Input() proposal?: ProposalRepresentation;
  @Input() language?: ProposalLanguageRepresentation;
  @Input() agreement?: AgreementRepresentation;
  @Input() myProfile?: ProfileRepresentation;

  private _subscription = new Subscription();

  verificationProperty: PropertyRepresentation | undefined;
  verificationPropertyValid = true;

  ngOnChanges(changes: SimpleChanges): void {
    if ((this.form && changes.form) || changes.language) {
      this._subscription?.unsubscribe();
      this._subscription = this.form.valueChanges.subscribe((value) => {
        const emailProperty = this._getFirstAgreementPropertyOfType(value, PropertyType.EMAIL);
        const mobileProperty = this._getFirstAgreementPropertyOfType(
          value,
          PropertyType.MOBILE_PHONE
        );

        const verificationPropertyControl = this.verificationPropertyForm;
        const verificationValueControl = this.verificationPropertyForm?.get('value');
        const verificationProperty = verificationPropertyControl?.value;

        if (verificationPropertyControl?.pristine) {
          const verificationProposalProperty = this.language?.verificationRequestedPropertyId
            ? this._getAgreementProperty(
                this.form.value,
                this.language?.verificationRequestedPropertyId
              )
            : undefined;

          this.verificationPropertyValid = propertyValid(verificationProposalProperty);
          if (verificationProposalProperty) {
            this.verificationProperty = verificationProposalProperty;
            if (
              hasValue(verificationProposalProperty) &&
              !hasEqualValue(verificationProperty, verificationProposalProperty)
            ) {
              verificationPropertyControl.setValue(verificationProposalProperty);
            }
          } else {
            this.verificationProperty = undefined;
            if (this.agreement?.verificationValue && verificationValueControl) {
              verificationValueControl.markAsDirty();
              verificationValueControl.setValue(this.agreement.verificationValue);
            } else {
              if (
                verificationProperty.type === PropertyType.EMAIL &&
                emailProperty?.value &&
                !hasEqualValue(verificationProperty, emailProperty)
              ) {
                verificationPropertyControl.setValue(emailProperty);
              }
              if (
                verificationProperty.type === PropertyType.MOBILE_PHONE &&
                mobileProperty?.value &&
                !hasEqualValue(verificationProperty, mobileProperty)
              ) {
                verificationPropertyControl.setValue(mobileProperty);
              }
            }
          }
        }
      });
    }
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
  }

  get possibleProperties(): Property[] {
    if (
      this.proposal?.verification === AgreementVerificationType.EMAIL ||
      this.proposal?.verification === AgreementVerificationType.MOBILE
    ) {
      let properties = <Property[]>(
        Subscribe.formProperties(this.form.value).filter(
          (property) => property.type === this.verificationType && hasValue(property)
        )
      );

      if (this.myProfile) {
        const profileProperties = <Property[]>getPropertyTuples(
          this.myProfile.propertyContexts,
          typeFilter(this.verificationType)
        )
          .map((tuple) => (tuple ? tuple.property : null))
          .filter((property) => !!property);
        properties = [...profileProperties, ...properties];
      }

      return properties;
    }

    return [];
  }

  get verificationForm() {
    return <UntypedFormGroup>this.form?.get('verification');
  }

  get verificationPropertyForm() {
    return this.verificationForm?.get('property');
  }

  get verificationType() {
    return this.verificationPropertyForm?.value?.type;
  }

  get isOwner() {
    return this.myProfile?.id === this.proposal?.ownerId;
  }

  get isReadonly() {
    return !!getPropertyDescription(this.language, this.language?.verificationRequestedPropertyId)
      ?.readonly;
  }

  private _getAgreementProperty(value: any, propertyId: string) {
    return Subscribe.formAgreementProperties(value).find(
      (p) => p.requestedPropertyId === propertyId
    )?.property;
  }

  private _getFirstAgreementPropertyOfType(value: any, type: PropertyType) {
    return Subscribe.formAgreementProperties(value)
      .filter((ap) => hasValue(ap.property))
      .filter((p) => p.property?.type === type)[0]?.property;
  }
}
